import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { SquaresFour, CaretDown, Sparkle, Waveform, Storefront } from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const LINKS = [
  { to: "/", label: "Beat Generator", hint: "Make a beat in seconds", icon: Sparkle, testid: "global-nav-generator" },
  { to: "/daw", label: "DAW", hint: "Full studio editor", icon: Waveform, testid: "global-nav-daw" },
  { to: "/beats", label: "Beat Store", hint: "License beats", icon: Storefront, testid: "global-nav-store" },
];

const isActive = (pathname, to) => (to === "/" ? pathname === "/" : pathname.startsWith(to));

export function GlobalNav({ light = false }) {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const triggerCls = light
    ? "bg-white border-neutral-300 text-neutral-900 hover:border-neutral-900"
    : "bg-[#0a0a0a] border-[#2a2a2a] text-white hover:border-white/50";
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          className={`flex items-center gap-1.5 h-9 px-3 rounded-full border text-xs font-bold tracking-wide flex-shrink-0 outline-none transition-colors ${triggerCls}`}
          data-testid="global-nav-trigger"
          aria-label="Open site menu"
        >
          <SquaresFour size={14} weight="bold" />
          Menu
          <CaretDown size={11} weight="bold" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={8}
        className="z-[2000] w-60 p-1.5 rounded-xl bg-[#0c0c0c] border border-[#2a2a2a] text-white shadow-2xl"
        data-testid="global-nav-menu"
      >
        {LINKS.map(({ to, label, hint, icon: Icon, testid }) => {
          const active = isActive(pathname, to);
          return (
            <DropdownMenuItem
              key={to}
              onSelect={() => {
                // Full reload in/out of the DAW so the audio engine never outlives its page.
                if (to === "/daw" || pathname.startsWith("/daw")) window.location.href = to;
                else nav(to);
              }}
              className={`flex items-center gap-3 px-2.5 py-2 rounded-lg cursor-pointer focus:bg-white/10 focus:text-white ${active ? "bg-emerald-500/10" : ""}`}
              data-testid={testid}
            >
              <span className={`w-8 h-8 rounded-md flex items-center justify-center ${active ? "bg-emerald-400 text-black" : "bg-white/5 text-white/80"}`}>
                <Icon size={15} weight="bold" />
              </span>
              <span className="flex flex-col">
                <span className="text-sm font-semibold leading-tight">{label}</span>
                <span className="text-[10px] text-white/40 font-mono">{hint}</span>
              </span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default GlobalNav;
